<template>
  <div class="view valet-registration" v-if="verified">
    <h6>{{ $t('valet.registration.breadcrumb') }}</h6>
    <form>
      <Radios
        ref="title"
        class="group large"
        :values="$t('registration.form.title')"
        :validator="form.validators.title"
        :value.sync="form.values.title"
        no-tracking
      />
      <div class="group half">
        <Input
          ref="firstname"
          type="text"
          :placeholder="$t('registration.form.firstname')"
          :validator="form.validators.firstname"
          :value.sync="form.values.firstname"
          no-tracking
          required
        />
        <Input
          ref="lastname"
          type="text"
          :placeholder="$t('registration.form.lastname')"
          :validator="form.validators.lastname"
          :value.sync="form.values.lastname"
          no-tracking
          required
        />
      </div>
      <div class="group twothirds">
        <Input
          ref="street"
          type="text"
          :placeholder="$t('registration.form.street')"
          :validator="form.validators.street"
          :value.sync="form.values.street"
          no-tracking
          required
        />
        <Input
          ref="streetnr"
          type="tel"
          :placeholder="$t('registration.form.streetnr')"
          :validator="form.validators.streetnr"
          :value.sync="form.values.streetnr"
          no-tracking
          required
        />
      </div>
      <div class="group onethird">
        <Input
          ref="zip"
          type="tel"
          :placeholder="$t('registration.form.zip')"
          :validator="form.validators.zip"
          :value.sync="form.values.zip"
          no-tracking
          required
        />
        <Input
          ref="city"
          type="text"
          :placeholder="$t('registration.form.city')"
          :validator="form.validators.city"
          :value.sync="form.values.city"
          no-tracking
          required
        />
      </div>
      <div class="group full">
        <Input
          ref="email"
          type="email"
          :placeholder="$t('registration.form.email')"
          :validator="form.validators.email"
          :value.sync="form.values.email"
          no-tracking
          required
        />
      </div>
      <div class="group half">
        <Input
          ref="mobile"
          type="tel"
          :placeholder="$t('registration.form.mobile')"
          :validator="form.validators.mobile"
          :value.sync="form.values.mobile"
          no-tracking
          required
        />
        <Input
          ref="plate"
          type="text"
          :placeholder="$t('registration.form.plate')"
          :validator="form.validators.plate"
          :value.sync="form.values.plate"
          no-tracking
          uppercase
          required
        />
      </div>
      <div class="group full">
        <Select
          ref="model"
          :placeholder="$t('registration.form.model.placeholder')"
          :hint="$t('registration.form.model.hint')"
          :options="$t('registration.form.model.options')"
          :value.sync="form.values.model"
          no-tracking
        />
      </div>
      <div class="group">
        <h3>{{ $t('registration.form.info.title') }}</h3>
        <p>{{ $t('registration.form.info.copy') }}</p>
        <Collapsable :title="$t('registration.form.consent.title')">
          <p>{{ $t('registration.form.consent.copy') }}</p>
        </Collapsable>
      </div>
      <Checkbox
        ref="terms"
        class="group"
        name="terms"
        :label="$t('registration.form.terms.text')"
        :value.sync="form.values.terms"
        :validator="form.validators.terms"
        no-tracking
      />
      <div class="group labeled-radios">
        <div v-for="option in $t('registration.form.terms.options')" :key="option.id">
          <div>{{ option.title }}</div>
          <Radios
            :ref="`channel${option.id}`"
            :name="option.id"
            :values="option.values"
            :value.sync="form.values[`channel${option.id}`]"
            :validator="form.validators[`channel${option.id}`]"
            no-tracking
          />
        </div>
      </div>
    </form>
    <div class="group submit">
      <span>{{ $t('registration.form.required') }}</span>
      <div>
        <Button gray use-arrow @click="handleCancelClick">{{ $t('valet.details.cancel') }}</Button>
        <Button link-name="form_send" link-category="form" @click="handleFormSubmit" use-arrow>{{ $t('valet.registration.submit') }}</Button>
      </div>
    </div>
  </div>
</template>

<script>
  import Input from '@/components/Input';
  import Collapsable from '@/components/Collapsable';
  import Button from '@/components/ui/Button';
  import Radios from '@/components/Radios';
  import Checkbox from '@/components/Checkbox';
  import UID from '@/utils/UID';
  import Select from '@/components/Select';

  export default {
    name: 'Registration',
    components: {
      Select,
      Checkbox,
      Radios,
      Button,
      Collapsable,
      Input,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {},
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      const min2CharsExp = /\S{2,}/;
      const min2CharsNoNumbersExp = /^[^0-9]{2,}$/;
      const notEmptyExp = /\S/;
      const exact4NumbersExp = /^[0-9]{4}$/;
      const emailExp = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      const phoneExp = /^[+0-9]{10,}$/;
      const notNullTest = value => value !== null;
      const trueTest = value => value === true;
      return {
        verified: false,
        form: {
          validators: {
            title: notNullTest,
            firstname: min2CharsExp,
            lastname: min2CharsExp,
            street: min2CharsNoNumbersExp,
            streetnr: notEmptyExp,
            zip: exact4NumbersExp,
            city: min2CharsExp,
            email: emailExp,
            mobile: phoneExp,
            plate: notEmptyExp,
            terms: trueTest,
            channelmail: notNullTest,
            channelemail: notNullTest,
            channelphone: notNullTest,
          },
          values: {
            title: null,
            firstname: null,
            lastname: null,
            street: null,
            streetnr: null,
            zip: null,
            city: null,
            email: null,
            mobile: null,
            plate: null,
            model: null,
            terms: null,
            channelmail: null,
            channelemail: null,
            channelphone: null,
          },
        },
        /*
        cars: [
          { value: 'Test1', label: 'Fahrzeug 1' },
          { value: 'Test2', label: 'Fahrzeug 2' },
          { value: 'Test3', label: 'Fahrzeug 3' },
          { value: 'Test4', label: 'Fahrzeug 4' },
          { value: 'Test5', label: 'Fahrzeug 5' },
          { value: 'Test6', label: 'Fahrzeug 6' },
        ],
        */
        preventStupidClick: false,
      };
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {},
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {},
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    filters: {},
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    async created() {
      if (!this.$b.data.verified) {
        this.$router.push(`/${this.$i18n.locale}/valet/login`);
      }
      this.verified = this.$b.data.verified;
    },
    // beforeMount() {},
    // render(h) { return h(); },
    mounted() {
      // this.$b.analytics.resetFormFields();
    },
    // beforeUpdate() {},
    // updated() {},
    // beforeDestroy() {},
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      validateFormData(form) {
        const values = form.values;
        const keys = Object.keys(values);

        let isValid = true;
        for (let i = 0, n = keys.length; i < n; i += 1) {
          const key = keys[i];
          const refs = this.$refs;
          if (refs[key]) {
            const ref = refs[key].constructor === Array ? refs[key][0] : refs[key];
            if (ref.validate && !ref.validate()) {
              isValid = false;
            }
          }
        }
        /*
        if (!isValid) {
          this.$b.analytics.trackFormError();
        }
        */
        return isValid;
      },
      //----------------------------------
      // Event Handlers
      //----------------------------------
      async handleFormSubmit(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        const form = this.form;
        const formIsValid = this.validateFormData(form);
        if (formIsValid && !this.preventStupidClick) {
          this.preventStupidClick = true;
          const time = Math.floor(Date.now() / 1000);
          await this.$b.api.post('global/register', null, {
            ...form.values,
            submitted: time,
            language: this.$i18n.locale,
            uid: UID.getAlphaId(),
            confirmed: 1,
            data: JSON.stringify({ checkin: time }),
          });
          this.$router.push(`/${this.$i18n.locale}/valet/overview`);
        }
      },
      handleCancelClick(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        this.$router.push(`/${this.$i18n.locale}/valet/overview`);
      },
    },
  };
</script>

<style lang="scss">
  @import '~scss/variables';
  @import '~scss/mixins';

  .view.valet-registration {
    padding: 25px 20px 0 20px;
    h1 {
      position: relative;
      @include font-daimler-cac();
      font-size: 3.4rem;
      margin: 0 0 55px;
      padding: 0;
    }
    h3 {
      @include font-daimler-cac();
      font-size: 2.8rem;
      margin: 45px 0 0 0;
      padding: 0;
    }
    h6 {
      position: relative;
      @include font-daimler-cs(400);
      font-size: 1.6rem;
      margin: 0;
      padding: 0;

      &:after {
        content: '';
        position: relative;
        display: block;
        height: 2px;
        width: 46px;
        background-color: $color-white;
        margin-top: 25px;
        margin-bottom: 30px;
      }
    }

    div.thanks {
      max-width: 622px;
      h1 {
        margin: 25px 0 25px;
      }
    }

    form {
      width: 100%;
      max-width: 590px;

      .group {
        margin-bottom: 16px;

        &.half, &.onethird, &.twothirds {
          display: flex;

          @include screen-mobile {
            flex-direction: column;
          }
        }

        &.half > div {
          width: 50%;
          margin: 0 10px;
          &:first-of-type { margin-left: 0; }
          &:last-of-type { margin-right: 0; }

          @include screen-mobile {
            margin: 0 0 16px 0;
            width: 100%;
            &:last-of-type {
              margin-bottom: 0;
            }
          }
        }

        &.onethird > div {
          margin: 0 10px;
          &:first-of-type { width: 33%; margin-left: 0; }
          &:last-of-type { width: 67%; margin-right: 0; }

          @include screen-mobile {
            margin: 0 0 16px 0;
            &:first-of-type { width: 100%; margin-left: 0; }
            &:last-of-type { width: 100%; margin-right: 0; margin-bottom: 0; }
          }
        }

        &.twothirds > div {
          margin: 0 10px;
          &:first-of-type { width: 67%; margin-left: 0; }
          &:last-of-type { width: 33%; margin-right: 0; }

          @include screen-mobile {
            margin: 0 0 16px 0;
            &:first-of-type { width: 100%; margin-left: 0; }
            &:last-of-type { width: 100%; margin-right: 0; margin-bottom: 0; }
          }
        }

        &.checkbox {
          margin-top: 38px;
        }

        &.labeled-radios {
          padding-top: 12px;
          margin-bottom: 40px;

          & > div {
            display: flex;
            font-size: 1.8rem;
            margin-bottom: 16px;
            & > div {
              min-width: 200px;
            }

            @include screen-mobile {
              display: block;
              & > div:first-of-type {
                margin-bottom: 16px;
              }
            }
          }
        }
      }
    }
    & > .group.submit {
      width: 100%;
      & > span {
        display: block;
        margin-bottom: 6px;
      }
      & > div {
        display: flex;
        justify-content: flex-end;
        text-align: right;

        button:first-of-type {
          margin-right: 32px;
        }
      }
    }
  }
</style>
